// src/components/CommonComponents/ChatHeads/VerifyDataModal.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Pencil, Check, ChevronDown, ChevronUp, Save } from "lucide-react";
import useLocalization from '../../../../../hooks/useLocalization';
import BotResponseText from "../../BotResponseComponent/BotResponseText";
import { useLogger } from "../../../../../utils/UnifiedLogger";
import { KpmgTransliterate } from "../../../TranslitrationSuggestion/KpmgTransliterate";
import { useAppContext } from "../../../../../context/AppContext";
import { getClientIP } from "../../../../../utils/util";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { API_ENDPOINTS } from '../../../../../utils/constants';
import ApiSearchDropdown from "../BotDynamicForm/ApiSearchDropdown";
import { useDispatch, useSelector } from "react-redux";
import { setIsGrievanceRegistering } from "../../../../../store/features/ui/uiSlice";
import { selectIsGrievanceRegistering } from "../../../../../store/features/ui/uiSelectors";
import EditSaveButton from "./EditSaveButton";

const VerifyDataModal = ({
    handleSubmitGrievance,
    botResponse,
    isOpen,
    closeModal,
    mandatory_data_present,
    updated_grievance = "",
    field_form = {},
    category_code,
    org_code,
    field_form_list,
    serverRemarks,
    grievance_bucket,
    isNotSubcategory=false
}) => {
    const { logInfo } = useLogger("VERIFY_DATA_MODAL", true);
    const dispatch = useDispatch();
    const isGrievanceRegistering = useSelector(selectIsGrievanceRegistering)
    const pendingPrefillRef = useRef({});
    const { selectedLanguageData, sharedApiService, userData } = useAppContext();
    const { t } = useLocalization();
    const firstLevelValueRef = useRef("");
    const botAdditionalFieldsRequest = sharedApiService.sendRequest;

    // ✅ State management
    const [formValues, setFormValues] = useState({});
    const [errors, setErrors] = useState({});
    const [unmatchedUserInputs, setUnmatchedUserInputs] = useState({}); // Track unmatched inputs
    const [isEditing, setIsEditing] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [isGrievanceExpanded, setIsGrievanceExpanded] = useState(false);

    // ✅ Field form state
    const [fieldForm, setFieldForm] = useState({ InputFields: [], DropDown: [] });
    const [globalLoading, setGlobalLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [isInitialPrefillComplete, setIsInitialPrefillComplete] = useState(false);

    // ✅ Dropdown states (like BotDynamicForm)
    const [openDropdowns, setOpenDropdowns] = useState({});
    const [searchTerms, setSearchTerms] = useState({});

    // ✅ Refs
    const userInfoSectionRef = useRef(null);
    const autoSaveTimeoutRef = useRef(null);
    const dropdownRefs = useRef({});
    const portalRefs = useRef({});

    console.log('botResponse',botResponse);
    
    // const grievance_bucket = botResponse?.grievance_bucket || {};
    // const grievance_bucket = botResponse?.grievance_bucket || grievanceBucket || {};

    // ✅ Close dropdown on click outside (TOP LEVEL useEffect)
    useEffect(() => {
        const handler = (e) => {
            Object.keys(openDropdowns).forEach(fieldName => {
                if (openDropdowns[fieldName]) {
                    const clickedMain = dropdownRefs.current[fieldName]?.contains(e.target);
                    const clickedPortal = portalRefs.current[fieldName]?.contains(e.target);

                    if (!clickedMain && !clickedPortal) {
                        setOpenDropdowns(prev => ({ ...prev, [fieldName]: false }));
                    }
                }
            });
        };

        const hasOpenDropdown = Object.values(openDropdowns).some(Boolean);
        if (hasOpenDropdown) {
            document.addEventListener("mousedown", handler);
            return () => document.removeEventListener("mousedown", handler);
        }
    }, [openDropdowns]);

    // ✅ Initialize field form with child fields (like BotDynamicForm)
    useEffect(() => {
        if (!isOpen || !field_form) return;

        let updatedForm = { ...field_form };
        const newInputFields = [...(updatedForm.InputFields || [])];
        const newDropDown = [...(updatedForm.DropDown || [])];

        // Check for parent fields with HasChilds: true but no child element
        newInputFields.forEach((field, idx) => {
            if (field.HasChilds === true) {
                const nextField = newInputFields[idx + 1];
                const isNextFieldChild = nextField && nextField.FieldOrder === field.FieldOrder + 1;

                if (!isNextFieldChild) {
                    const childField = {
                        ...field,
                        FieldName: `${field.FieldName}_child`,
                        DisplayLable: `Additional Fields`,
                        DisplayLable_regional: `${field.DisplayLable_regional || field.DisplayLable} - Child`,
                        FieldOrder: field.FieldOrder + 1,
                        IsMandatory: false,
                        options: [],
                        HasChilds: false,
                        FieldType: 'Dropdown'
                    };

                    newInputFields.splice(idx + 1, 0, childField);
                    newDropDown.push([]);
                }
            }
        });

        newInputFields.sort((a, b) => a.FieldOrder - b.FieldOrder);

        updatedForm.InputFields = newInputFields;
        updatedForm.DropDown = newDropDown;

        setFieldForm(updatedForm);
        setIsInitialPrefillComplete(false);
    }, [isOpen, field_form]);

    // ✅ Get dropdown index by field name
    const getDropdownIndexByFieldName = useCallback((fieldName) => {
        const dropdownFields = fieldForm.InputFields.filter(f =>
            f.FieldType?.toLowerCase() === 'dropdown'
        );
        return dropdownFields.findIndex(f => f.FieldName === fieldName);
    }, [fieldForm.InputFields]);

    // ✅ Get dropdown options for a field
    const getDropdownOptions = useCallback((field) => {
        const dropdownIndex = getDropdownIndexByFieldName(field.FieldName);
        const options = fieldForm.DropDown?.[dropdownIndex] || [];
        return options.filter(opt => opt.Value !== "" || opt.Text !== "");
    }, [fieldForm.DropDown, getDropdownIndexByFieldName]);

    // ✅ Call API for child options
    const callAdditionalFieldsAPI = useCallback(async (fieldObj, value, isFromPrefill = false, prefillChildValue = null, prefillChildText = null) => {
        logInfo('CALLING_ADDITIONAL_FIELDS_API', 'Calling API for additional fields', {
            fieldName: fieldObj.FieldName,
            parentValue: value,
            isFromPrefill,
            prefillChildValue,
            prefillChildText
        });

        setGlobalLoading(true);
        setApiError(null);

        try {
            const sortedFields = [
                ...(fieldForm.InputFields?.filter(
                    f => f.FieldType?.toLowerCase() === 'dropdown'
                ) || [])
            ].sort((a, b) => a.FieldOrder - b.FieldOrder);
            // First field
            const firstLevelField = sortedFields?.[0];

            // Base/initial firstLevelValue logic
            let firstLevelValue = firstLevelValueRef.current;

            if (firstLevelField) {
                if (firstLevelField.FieldType.toLowerCase() === "dropdown") {
                    firstLevelValue = formValues[firstLevelField.FieldName];
                }
            }


            if (firstLevelValue === undefined || firstLevelValue === null || firstLevelValue === "" && Object.keys(firstLevelField).length > 0) {
                firstLevelValue = firstLevelField.UserInputValue || firstLevelField.FieldValue || "";
            }

            // If current field is the first field, use parentValue
            if (fieldObj.FieldName === firstLevelField?.FieldName &&
                firstLevelField.FieldType?.toLowerCase() === 'dropdown') {
                firstLevelValue = value;
            }

            await botAdditionalFieldsRequest({
                method: "GET",
                url: `${API_ENDPOINTS.getAdditionalDropdownValueList}?language=${selectedLanguageData?.code}&orgCode=${org_code}&firstLevelInputFieldDropdownValue=${firstLevelValue}&parentDropdownFieldName=${fieldObj.FieldName}&parentDropdownValue=${value}&currentCategory=${category_code}`,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `CPGRAMS Q0hCT1Q6VEhZR0JINzY4OTBKSFlBV1Na...`
                },
                abortable: true,
                isEncrypted: false,

                onSuccess: (res) => {
                    setGlobalLoading(false);

                    if (!Array.isArray(res) || res.length === 0) return;

                    const formattedOptions = res
                        .map(item => {
                            const isEnglish = selectedLanguageData?.code?.toLowerCase() === "en";
                            const regionalText = typeof item.Value_regional === "string"
                                ? item.Value_regional.replace(/^\[?'?/, '').replace(/'?\]?$/, '').trim()
                                : item.Value_regional;
                            const displayText = isEnglish
                                ? (item.Value || item.text || item.name || "")
                                : (regionalText || item.Value || "");

                            const isSelected = prefillChildValue &&
                                (item.Key === prefillChildValue || item.Value === prefillChildValue);

                            return {
                                Value: item.Key || item.Value || "",
                                Text: displayText,
                                Text_regional: regionalText,
                                Disabled: false,
                                Selected: isSelected,
                                isSelected: isSelected
                            };
                        })
                        .filter(opt => opt.Value !== "" && opt.Text !== "");

                    logInfo('ADDITIONAL_FIELD_OPTIONS_LOADED', 'Child options loaded from API', {
                        fieldName: fieldObj.FieldName,
                        parentValue: value,
                        optionsCount: formattedOptions.length
                    });

                    setFieldForm(prev => {
                        const updatedForm = { ...prev };
                        const dropdownFields = prev.InputFields.filter(f =>
                            f.FieldType?.toLowerCase() === 'dropdown'
                        );
                        const parentDropdownIndex = dropdownFields.findIndex(f =>
                            f.FieldName === fieldObj.FieldName
                        );
                        const childDropdownIndex = parentDropdownIndex + 1;

                        // Update DropDown array
                        if (updatedForm.DropDown && childDropdownIndex < updatedForm.DropDown.length) {
                            updatedForm.DropDown = [...updatedForm.DropDown];
                            updatedForm.DropDown[childDropdownIndex] = formattedOptions;
                        }

                        // Update InputFields
                        updatedForm.InputFields = updatedForm.InputFields.map((f, idx) => {
                            const fieldDropdownIndex = dropdownFields.findIndex(df => df.FieldName === f.FieldName);
                            if (fieldDropdownIndex === childDropdownIndex) {
                                return {
                                    ...f,
                                    options: formattedOptions,
                                    IsHidden: false
                                };
                            }
                            return f;
                        });

                        return updatedForm;
                    });

                    // --- APPLY PENDING PREFILL ONCE OPTIONS ARE LOADED ---
                    const pendingValue = pendingPrefillRef.current[fieldObj.FieldName];
                    if (pendingValue) {
                        const existsInOptions = formattedOptions.some(opt =>
                            opt.Value?.toString().trim().toLowerCase() === pendingValue.toString().trim().toLowerCase()
                        );

                        if (existsInOptions) {
                            setFormValues(prev => ({
                                ...prev,
                                [fieldObj.FieldName]: pendingValue
                            }));

                            delete pendingPrefillRef.current[fieldObj.FieldName];

                            logInfo('PREFILL_APPLIED_AFTER_OPTIONS', 'Prefilled dropdown value applied after options loaded', {
                                fieldName: fieldObj.FieldName,
                                value: pendingValue
                            });
                        }
                    }


                    // Handle child value matching after options are loaded
                    if (prefillChildValue || prefillChildText) {
                        const childFieldName = `${fieldObj.FieldName}_child`;

                        // Try to match by Value first
                        let matchedOption = formattedOptions.find(opt =>
                            opt.Value?.toString().trim().toLowerCase() === prefillChildValue?.toString().trim().toLowerCase()
                        );

                        // If no match by Value, try by Text
                        if (!matchedOption && prefillChildText) {
                            matchedOption = formattedOptions.find(opt =>
                                opt.Text?.trim().toLowerCase() === prefillChildText.trim().toLowerCase() ||
                                opt.Text_regional?.trim().toLowerCase() === prefillChildText.trim().toLowerCase()
                            );
                        }

                        if (matchedOption) {
                            // Matched - set the value and clear unmatched
                            setFormValues(prevValues => ({
                                ...prevValues,
                                [childFieldName]: matchedOption.Value
                            }));
                            setUnmatchedUserInputs(prev => {
                                const updated = { ...prev };
                                delete updated[childFieldName];
                                return updated;
                            });
                            logInfo('CHILD_VALUE_MATCHED', 'Child value matched after API load', {
                                childFieldName,
                                matchedValue: matchedOption.Value
                            });
                        } else {
                            // Not matched - keep unmatched text visible
                            setFormValues(prevValues => ({
                                ...prevValues,
                                [childFieldName]: ""
                            }));
                            if (prefillChildText) {
                                setUnmatchedUserInputs(prev => ({
                                    ...prev,
                                    [childFieldName]: prefillChildText
                                }));
                            }
                            logInfo('CHILD_VALUE_UNMATCHED', 'Child value did not match any option', {
                                childFieldName,
                                prefillChildValue,
                                prefillChildText,
                                availableOptions: formattedOptions.length
                            });
                        }
                    }
                },

                onError: (err) => {
                    console.log("Error fetching additional fields:", err);
                    setGlobalLoading(false);
                    if (!isFromPrefill) {
                        setApiError("Failed to load dependent fields. Please retry.");
                    }
                },
            });
        } catch (error) {
            console.error("Exception in fetching additional fields:", error);
            setGlobalLoading(false);
            if (!isFromPrefill) {
                setApiError("Something went wrong. Please retry.");
            }
        }
    }, [fieldForm.InputFields, formValues, selectedLanguageData?.code, org_code, category_code, botAdditionalFieldsRequest, logInfo]);

    // ✅ Extract value from response
    const extractValueFromResponse = useCallback((field, dropdownOptions = []) => {
        if (!field.response) return null;

        const allOptions = [
            ...(field.options || []),
            ...(dropdownOptions || [])
        ];

        const matchedOption = allOptions.find(opt =>
            opt.Text === field.response ||
            opt.Text?.toLowerCase() === field.response?.toLowerCase() ||
            opt.Value === field.response ||
            opt.Value?.toLowerCase() === field.response?.toLowerCase()
        );

        return matchedOption ? matchedOption.Value : null;
    }, []);

    // ✅ Prefill form values
    useEffect(() => {
        if (!isOpen || !fieldForm.InputFields?.length || isInitialPrefillComplete) return;

        const prefilled = {};
        const apiCallsToMake = [];
        const unmatchedInputs = {}; // Track unmatched user inputs

        // Make all fields visible initially
        setFieldForm(prev => {
            const updatedForm = { ...prev };
            updatedForm.InputFields = updatedForm.InputFields.map(field => ({
                ...field,
                IsHidden: false
            }));
            return updatedForm;
        });

        // Method 1: Prefill from field_form_list
        if (Array.isArray(field_form_list) && field_form_list.length > 0) {
            field_form_list.forEach(fieldInfo => {
                if (fieldInfo.UserInputValue || fieldInfo.UserInputText) {
                    const userInputValue = fieldInfo.UserInputValue || "";
                    const userInputText = fieldInfo.UserInputText || "";

                    const field = fieldForm.InputFields?.find(f => f.FieldName === fieldInfo.FieldName);

                    if (field && field.FieldType?.toLowerCase() === 'dropdown') {
                        // Get dropdown options for this field
                        const dropdownIndex = getDropdownIndexByFieldName(fieldInfo.FieldName);
                        const dropdownOptions = fieldForm.DropDown?.[dropdownIndex] || [];

                        // Try to match by Value first
                        let matchedOption = dropdownOptions.find(opt =>
                            opt.Value?.toString().trim().toLowerCase() === userInputValue?.toString().trim().toLowerCase()
                        );

                        // If no match by Value, try by Text
                        if (!matchedOption && userInputText) {
                            matchedOption = dropdownOptions.find(opt =>
                                opt.Text?.trim().toLowerCase() === userInputText.trim().toLowerCase() ||
                                opt.Text_regional?.trim().toLowerCase() === userInputText.trim().toLowerCase()
                            );
                        }

                        if (matchedOption) {
                            // Matched - set the value
                            prefilled[fieldInfo.FieldName] = matchedOption.Value;
                            logInfo('PREFILL_MATCHED', 'User input matched dropdown option', {
                                fieldName: fieldInfo.FieldName,
                                userInputValue,
                                userInputText,
                                matchedValue: matchedOption.Value
                            });
                        } else {
                            // Not matched - store as unmatched input to show above field
                            prefilled[fieldInfo.FieldName] = ""; // Keep field empty
                            unmatchedInputs[fieldInfo.FieldName] = userInputText || userInputValue;
                            logInfo('PREFILL_UNMATCHED', 'User input did not match any dropdown option', {
                                fieldName: fieldInfo.FieldName,
                                userInputValue,
                                userInputText,
                                availableOptions: dropdownOptions.length
                            });
                        }
                    } else {
                        // Non-dropdown field - just set the value
                        prefilled[fieldInfo.FieldName] = userInputValue || userInputText || "";
                    }

                    // Handle child value
                    if (fieldInfo.UserInputChildValue || fieldInfo.UserInputChildText) {
                        const childFieldName = `${fieldInfo.FieldName}_child`;
                        const childUserInputValue = fieldInfo.UserInputChildValue || "";
                        const childUserInputText = fieldInfo.UserInputChildText || "";

                        // Child options may not be loaded yet, store for later matching
                        prefilled[childFieldName] = childUserInputValue || childUserInputText || "";

                        // Store child unmatched text for display (will be cleared if matched after API call)
                        if (childUserInputText) {
                            unmatchedInputs[childFieldName] = childUserInputText;
                        }
                    }

                    if (field && (field.HasChilds || fieldInfo.UserInputChildValue) && (prefilled[fieldInfo.FieldName] || userInputValue)) {
                        apiCallsToMake.push({
                            field,
                            value: prefilled[fieldInfo.FieldName] || userInputValue,
                            childValue: fieldInfo.UserInputChildValue,
                            childText: fieldInfo.UserInputChildText,
                            order: field.FieldOrder
                        });
                    }
                }
            });
        }

        // Method 2: Prefill from InputFields
        fieldForm.InputFields?.forEach((field) => {
            if (prefilled[field.FieldName] !== undefined) return; // Skip if already prefilled

            let valueToSet = null;
            let userProvidedText = null;

            if (field.UserInputValue !== null && field.UserInputValue !== undefined) {
                valueToSet = field.UserInputValue;
                userProvidedText = field.UserInputText;
            }
            else if (field.UserInputText !== null && field.UserInputText !== undefined) {
                valueToSet = field.UserInputText;
                userProvidedText = field.UserInputText;
            }
            else if (field.response) {
                valueToSet = field.response;
                userProvidedText = field.response;
            }
            else if (field.FieldValue) {
                valueToSet = field.FieldValue;
            }

            if (valueToSet !== null) {
                if (field.FieldType?.toLowerCase() === 'dropdown') {
                    const dropdownIndex = getDropdownIndexByFieldName(field.FieldName);
                    const dropdownOptions = fieldForm.DropDown?.[dropdownIndex] || [];

                    // Try to match
                    let matchedOption = dropdownOptions.find(opt =>
                        opt.Value?.toString().trim().toLowerCase() === valueToSet?.toString().trim().toLowerCase() ||
                        opt.Text?.trim().toLowerCase() === valueToSet?.toString().trim().toLowerCase()
                    );

                    if (matchedOption) {
                        prefilled[field.FieldName] = matchedOption.Value;
                    } else {
                        prefilled[field.FieldName] = "";
                        if (userProvidedText) {
                            unmatchedInputs[field.FieldName] = userProvidedText;
                        }
                    }
                } else {
                    prefilled[field.FieldName] = valueToSet;
                }

                if (field.UserInputChildValue) {
                    prefilled[`${field.FieldName}_child`] = field.UserInputChildValue;
                    if (field.UserInputChildText) {
                        unmatchedInputs[`${field.FieldName}_child`] = field.UserInputChildText;
                    }
                }

                if (field.FieldOrder === 1) {
                    firstLevelValueRef.current = prefilled[field.FieldName] || valueToSet;
                }

                if (field.HasChilds || field.UserInputChildValue) {
                    apiCallsToMake.push({
                        field,
                        value: prefilled[field.FieldName] || valueToSet,
                        childValue: field.UserInputChildValue,
                        childText: field.UserInputChildText,
                        order: field.FieldOrder
                    });
                }
            }
        });

        logInfo('PREFILL_VALUES', 'Setting pre-filled values', {
            prefilled,
            unmatchedInputs,
            apiCallsToMake: apiCallsToMake.length
        });

        if (Object.keys(prefilled).length > 0) {
            setFormValues(prev => ({ ...prev, ...prefilled }));
        }

        if (Object.keys(unmatchedInputs).length > 0) {
            setUnmatchedUserInputs(unmatchedInputs);
        }

        if (apiCallsToMake.length > 0) {
            const sortedCalls = [...apiCallsToMake].sort((a, b) => a.order - b.order);
            sortedCalls.forEach(({ field, value, childValue, childText }) => {
                callAdditionalFieldsAPI(field, value, true, childValue, childText);
            });
        }

        setIsInitialPrefillComplete(true);
    }, [isOpen, fieldForm, field_form_list, isInitialPrefillComplete, getDropdownIndexByFieldName, extractValueFromResponse, callAdditionalFieldsAPI, logInfo]);

    // ✅ Handle change
    const handleChange = useCallback((field, value) => {
        logInfo('FIELD_VALUE_CHANGED', 'Field value changed', {
            fieldName: field.FieldName,
            newValue: value
        });

        const fieldName = field.FieldName;

        setFormValues(prev => ({ ...prev, [fieldName]: value }));
        setErrors(prev => ({ ...prev, [fieldName]: '' }));

        // Clear unmatched input when user selects a valid option
        if (value && unmatchedUserInputs[fieldName]) {
            setUnmatchedUserInputs(prev => {
                const updated = { ...prev };
                delete updated[fieldName];
                return updated;
            });
        }

        if (isEditing) {
            setHasUnsavedChanges(true);
        }

        if (field.FieldOrder === 1) {
            firstLevelValueRef.current = value;
        }


        if (field.HasChilds) {
            const currentOrder = field.FieldOrder;

            // Find only dropdown children
            const childDropdownFields = fieldForm.InputFields.filter(
                f => f.FieldOrder > currentOrder && f.FieldType?.toLowerCase() === "dropdown"
            );

            // Clear only dropdown values
            setFormValues(prev => {
                const updated = { ...prev };
                childDropdownFields.forEach(child => delete updated[child.FieldName]);
                return updated;
            });

            // Reset only dropdown option lists + hide them
            setFieldForm(prev => {
                const updated = { ...prev };
                childDropdownFields.forEach(child => {
                    const idx = getDropdownIndexByFieldName(child.FieldName);
                    if (idx !== -1) updated.DropDown[idx] = [];

                    updated.InputFields = updated.InputFields.map(f =>
                        f.FieldName === child.FieldName
                            ? { ...f, options: [], IsHidden: true }
                            : f
                    );
                });
                return updated;
            });

            // Close dropdown UI
            setOpenDropdowns(prev => {
                const updated = { ...prev };
                childDropdownFields.forEach(child => (updated[child.FieldName] = false));
                return updated;
            });

            // Clear search terms for dropdowns only
            setSearchTerms(prev => {
                const updated = { ...prev };
                childDropdownFields.forEach(child => (updated[child.FieldName] = ""));
                return updated;
            });

            // Fetch next-level dropdowns
            if (value) {
                callAdditionalFieldsAPI(field, value, false);
            }
        }
    }, [isEditing, unmatchedUserInputs, callAdditionalFieldsAPI, logInfo]);

    // ✅ Field validation
    const validateField = useCallback((field) => {
        const value = formValues[field.FieldName] || '';
        if (field.IsMandatory && !value) {
            return t('requiredField') || 'This field is required';
        }
        return '';
    }, [formValues, t]);

    // ✅ Form validation
    const validateForm = useCallback(() => {
        const newErrors = {};
        let isValid = true;

        fieldForm.InputFields.forEach(field => {
            if (!field.IsHidden) {
                const error = validateField(field);
                if (error) {
                    newErrors[field.FieldName] = error;
                    isValid = false;
                }
            }
        });

        setErrors(newErrors);
        return isValid;
    }, [fieldForm.InputFields, validateField]);

    // ✅ Check if field is enabled
    const isFieldEnabled = useCallback((field) => {
        if (!isEditing) return false;

        if (field.FieldOrder > 1) {
            const parentField = fieldForm.InputFields.find(f =>
                f.FieldOrder === field.FieldOrder - 1 && f.HasChilds
            );

            if (parentField && !formValues[parentField.FieldName]) {
                return false;
            }
        }

        return true;
    }, [isEditing, fieldForm.InputFields, formValues]);

    // ✅ Render dropdown (like BotDynamicForm)
    const renderDropdown = useCallback((field) => {
        const dropdownOptions = getDropdownOptions(field);
        const isEnabled = isFieldEnabled(field);
        const currentValue = formValues[field.FieldName] || "";
        const isRegional = selectedLanguageData?.code?.toLowerCase() !== "en";
        const isOpen = openDropdowns[field.FieldName] || false;
        const searchTerm = searchTerms[field.FieldName] || "";
        const hasUnmatchedInput = unmatchedUserInputs[field.FieldName];

        const selectedOption = dropdownOptions.find(opt =>
            opt.Value?.toString() === currentValue?.toString()
        );

        let displayValue = "";

        if (selectedOption) {
            if (isRegional) {
                displayValue =
                    selectedOption.Text_regional || selectedOption.Text;
            } else {
                displayValue = selectedOption.Text;
            }
        }


        const filteredOptions = dropdownOptions.filter(option => {
            const displayText = isRegional
                ? (option.Text_regional || option.Text || "")
                : (option.Text || "");
            return displayText.toLowerCase().includes(searchTerm.toLowerCase());
        });

        const handleSelect = (value) => {
            handleChange(field, value);
            setOpenDropdowns(prev => ({ ...prev, [field.FieldName]: false }));
            setSearchTerms(prev => ({ ...prev, [field.FieldName]: "" }));
        };

        const getPortalStyle = () => {
            if (!dropdownRefs.current[field.FieldName]) return {};

            const rect = dropdownRefs.current[field.FieldName].getBoundingClientRect();
            const approxHeight = 240;

            const spaceAbove = rect.top;
            const spaceBelow = window.innerHeight - rect.bottom;

            const top = spaceAbove > approxHeight && spaceBelow < approxHeight
                ? rect.top - approxHeight - 8
                : rect.bottom + 8;

            return {
                left: rect.left,
                top,
                width: rect.width
            };
        };

        return (
            <div key={field.FieldName} className="relative w-full">
                {/* Show unmatched user input warning */}
                {hasUnmatchedInput && (
                    <div className="mb-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-md">
                        <p className="text-xs text-gray-700 dark:text-gray-300">
                            <span className="font-medium">{'User entered data'}: </span>
                            <span className="text-amber-700 dark:text-amber-400 font-semibold">"{hasUnmatchedInput}"</span>
                        </p>
                    </div>
                )}

                <div
                    ref={el => (dropdownRefs.current[field.FieldName] = el)}
                    className={`border rounded-md px-3 py-2 ${isEnabled ? "cursor-pointer bg-white dark:bg-gray-800" : "bg-gray-100 dark:bg-gray-700 cursor-not-allowed"} ${hasUnmatchedInput && !currentValue ? "border-amber-400" : "border-gray-300"} flex justify-between items-center`}
                    onClick={() => isEnabled && setOpenDropdowns(prev => ({ ...prev, [field.FieldName]: !isOpen }))}
                >
                    <span className={!currentValue ? "text-gray-400" : "text-gray-900 dark:text-white"}>
                        {displayValue || "Select..."}
                    </span>

                    <svg
                        className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 9l-7 7-7-7" />
                    </svg>
                </div>

                {isOpen && isEnabled && createPortal(
                    <div
                        ref={el => (portalRefs.current[field.FieldName] = el)}
                        className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-2xl max-h-60 overflow-auto"
                        style={getPortalStyle()}
                    >
                        <div className="sticky top-0 bg-white dark:bg-gray-800 p-2 border-b">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerms(prev => ({ ...prev, [field.FieldName]: e.target.value }))}
                                className="w-full px-2 py-1 border rounded text-sm dark:bg-gray-700 dark:text-white"
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>

                        <div className="max-h-48 overflow-y-auto">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((opt, i) => (
                                    <div
                                        key={opt.Value}
                                        className={`px-3 py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700 ${opt.Value === currentValue ? "bg-blue-100 dark:bg-blue-900" : ""}`}
                                        onClick={() => !opt.Disabled && handleSelect(opt.Value)}
                                    >
                                        <span className="text-gray-900 dark:text-white">
                                            {isRegional
                                                ? (opt.Text_regional || opt.Text)
                                                : opt.Text}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <div className="p-3 text-center text-gray-500">No options found</div>
                            )}
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        );
    }, [getDropdownOptions, isFieldEnabled, formValues, selectedLanguageData?.code, openDropdowns, searchTerms, unmatchedUserInputs, handleChange, t]);

    const handleTextChange = useCallback(() => { }, []);

    // ✅ Render field
    const renderField = useCallback((field) => {
        const value = formValues[field.FieldName] || "";
        const hasError = errors[field.FieldName];
        const isDisabled = !isEditing || !isFieldEnabled(field);
        const fieldType = field.FieldType?.toLowerCase() || 'text';

        const getInputClasses = (extraClasses = "") => {
            return `w-full px-3 py-2 rounded border transition-all duration-200 ${hasError
                ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                } ${isEditing && !isDisabled
                    ? "text-gray-900 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-2"
                    : "bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                } ${extraClasses}`;
        };

        if (fieldType === "dropdown") {
            if (field.IsSearchable) {
                const hasUnmatchedInput = unmatchedUserInputs[field.FieldName];
                return (
                    <div>
                        {hasUnmatchedInput && (
                            <div className="mb-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-md">
                                <p className="text-xs text-gray-700 dark:text-gray-300">
                                    <span className="font-medium">{'User entered data'}: </span>
                                    <span className="text-amber-700 dark:text-amber-400 font-semibold">
                                        "{hasUnmatchedInput}"
                                    </span>
                                </p>
                            </div>
                        )}
                        <ApiSearchDropdown
                            hasUnmatchedInput={hasUnmatchedInput}
                            field={field}
                            onChange={handleChange}
                        />
                    </div>
                );
            } else {
                return renderDropdown(field);
            }
        }

        if (fieldType === 'textbox' || fieldType === 'text') {
            if (selectedLanguageData?.code !== 'en' && isEditing) {
                return (
                    <KpmgTransliterate
                        type="text"
                        lang={selectedLanguageData?.code || 'en'}
                        className={getInputClasses()}
                        value={value}
                        placeholder={selectedLanguageData?.code === "en" ? field.DisplayLable : field.DisplayLable_regional}
                        onChange={(e) => handleChange(field, e.target.value)}
                        disabled={!isEditing}
                        enabled={selectedLanguageData?.code !== "en"}
                        onChangeText={(e) => handleTextChange(field.FieldName, e.value)}
                    />
                );
            }

            return (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => handleChange(field, e.target.value)}
                    disabled={isDisabled}
                    placeholder={selectedLanguageData?.code === "en" ? field.DisplayLable : field.DisplayLable_regional}
                    className={getInputClasses()}
                />
            );
        }

        if (fieldType === 'textarea') {
            return (
                <textarea
                    value={value}
                    onChange={(e) => handleChange(field, e.target.value)}
                    disabled={isDisabled}
                    placeholder={selectedLanguageData?.code === "en" ? field.DisplayLable : field.DisplayLable_regional}
                    className={getInputClasses()}
                    rows={4}
                />
            );
        }

        return (
            <input
                type="text"
                value={value}
                onChange={(e) => handleChange(field, e.target.value)}
                disabled={isDisabled}
                placeholder={selectedLanguageData?.code === "en" ? field.DisplayLable : field.DisplayLable_regional}
                className={getInputClasses()}
            />
        );
    }, [formValues, errors, isEditing, isFieldEnabled, selectedLanguageData?.code, renderDropdown, handleChange, handleTextChange]);

    // ✅ Auto-save functionality
    const autoSave = useCallback(async () => {
        if (!hasUnsavedChanges || !isEditing) return;

        setIsAutoSaving(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1200));
            setHasUnsavedChanges(false);
        } catch (error) {
            logInfo('AUTO_SAVE_ERROR', 'Auto-save failed', { error });
        } finally {
            setIsAutoSaving(false);
        }
    }, [hasUnsavedChanges, isEditing, logInfo]);

    // ✅ Auto-save effect
    useEffect(() => {
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        if (hasUnsavedChanges && isEditing) {
            autoSaveTimeoutRef.current = setTimeout(() => {
                autoSave();
            }, 2000);
        }

        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [hasUnsavedChanges, isEditing, autoSave]);

    // ✅ Focus on open
    useEffect(() => {
        if (isOpen && userInfoSectionRef.current) {
            const timer = setTimeout(() => {
                if (userInfoSectionRef.current) {
                    userInfoSectionRef.current.focus();
                    userInfoSectionRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && userInfoSectionRef.current && !globalLoading && isInitialPrefillComplete) {
            const timer = setTimeout(() => {
                if (userInfoSectionRef.current) {
                    console.log('SCROLLING_TO_USER_INFO_SECTION');

                    // First ensure the element is visible and in DOM
                    userInfoSectionRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest'
                    });

                    // Then focus with a small delay
                    setTimeout(() => {
                        if (userInfoSectionRef.current) {
                            userInfoSectionRef.current.focus();

                            // Add a visual indicator
                            userInfoSectionRef.current.style.boxShadow = '0 0 0 3px #3b82f6';
                            userInfoSectionRef.current.style.borderRadius = '8px';
                            userInfoSectionRef.current.style.transition = 'box-shadow 0.3s ease';

                            setTimeout(() => {
                                if (userInfoSectionRef.current) {
                                    userInfoSectionRef.current.style.boxShadow = '';
                                }
                            }, 2000);
                        }
                    }, 100);
                }
            }, 100); // Reduced delay since prefill is already complete

            return () => clearTimeout(timer);
        }
    }, [isOpen, globalLoading, isInitialPrefillComplete]);

    // ✅ Edit/Save toggle handler
    // const handleEditSaveToggle = async () => {
    //     if (isEditing) {
    //         if (!validateForm()) {
    //             logInfo('SAVE_VALIDATION_FAILED', 'Form validation failed on save');
    //             return;
    //         }

    //         setIsSaving(true);
    //         try {
    //             await new Promise(resolve => setTimeout(resolve, 800));
    //             setHasUnsavedChanges(false);
    //             setIsEditing(false);
    //             logInfo('MANUAL_SAVE_COMPLETED', 'Form manually saved successfully', formValues);
    //         } catch (error) {
    //             logInfo('MANUAL_SAVE_ERROR', 'Manual save failed', { error });
    //         } finally {
    //             setIsSaving(false);
    //         }
    //     } else {
    //         setIsEditing(true);
    //     }
    // };

    // ✅ Submit handler
    const handleSubmit = async () => {
        logInfo('SUBMIT_STARTED', 'Starting form submission', { formValues });

        if (!validateForm()) {
            logInfo('SUBMIT_VALIDATION_FAILED', 'Form validation failed on submit');
            return;
        }

        dispatch(setIsGrievanceRegistering(true));
        setIsSubmitting(true);
        try {
            const dropdownFields = fieldForm.InputFields
                .filter(field =>
                    field.FieldType?.toLowerCase() === 'dropdown' &&
                    !field.IsHidden
                )
                .sort((a, b) => a.FieldOrder - b.FieldOrder);

            const lastDropdownField = dropdownFields[dropdownFields.length - 1];
            const finalDropdownListValue = lastDropdownField
                ? (formValues[lastDropdownField.FieldName] || "")
                : "";

            let additionalData = {
                OrgCode: org_code,
                FinalCategory: category_code,
                FinalDropdownListValue: finalDropdownListValue,
                Remarks: serverRemarks || updated_grievance,
                IpAddress: await getClientIP(),
                Document: "",
                Language: selectedLanguageData?.code || "en",
                UserCode: userData?.id
            };

            await handleSubmitGrievance(formValues, additionalData);

            logInfo('SUBMIT_COMPLETED', 'Form submitted successfully', { formValues });
        } catch (error) {
            console.log(`Error is`, error);
            logInfo('SUBMIT_ERROR', 'Form submission failed', { error });
        } finally {
            setIsSubmitting(false);
            // dispatch(setIsGrievanceRegistering(false));
        }
    };

    const toggleGrievanceExpansion = () => {
        setIsGrievanceExpanded(prev => !prev);
    };

    // ✅ Early return if modal not open
    if (!isOpen) return null;

    const allFields = [...fieldForm.InputFields].sort((a, b) => a.FieldOrder - b.FieldOrder);

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black bg-opacity-40">
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-[90vw] sm:w-[75vw] md:w-1/2 relative flex flex-col max-h-[80%]">
                    <h2 className="sticky top-0 text-xl font-semibold mb-4 px-5 py-3 bg-gradient-to-b from-[#0F2FA0] via-[#0F2FA0] to-[#007E86] text-white rounded-t-lg">
                        {t('verifyDetails')}
                    </h2>

                    {/* SUBMIT LOADER OVERLAY */}
                    {isGrievanceRegistering && (
                        <div className="absolute inset-0 z-[9999] flex items-center justify-center bg-white/80 dark:bg-black/70">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl px-6 py-5 flex flex-col items-center gap-3">
                                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Registering grievance, please wait...
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex-1 overflow-auto relative">
                        {/* Collapsible Grievance Summary */}
                        <div className="px-5 py-2">
                            <div className="bg-indigo-50 dark:bg-indigo-800 rounded-lg border border-indigo-200 dark:border-indigo-600 shadow-sm">
                                <button
                                    onClick={toggleGrievanceExpansion}
                                    className="w-full flex items-center justify-between p-4 hover:bg-indigo-100 dark:hover:bg-indigo-700 transition-colors duration-200 rounded-lg"
                                >
                                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                                        {t('grievanceSummery')}
                                    </p>
                                    {isGrievanceExpanded ? (
                                        <ChevronUp size={20} className="text-gray-600 dark:text-gray-400" />
                                    ) : (
                                        <ChevronDown size={20} className="text-gray-600 dark:text-gray-400" />
                                    )}
                                </button>

                                {isGrievanceExpanded && (
                                    <div className="px-4 pb-4 transition-all duration-300 ease-in-out">
                                        <div className="text-sm text-gray-800 dark:text-gray-100 mt-1 whitespace-pre-line bg-white dark:bg-gray-800 p-3 rounded border">
                                            <BotResponseText text={updated_grievance || "No summary available."} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {mandatory_data_present && grievance_bucket && Object.keys(grievance_bucket).length > 0 && (
                            <div className="bg-white dark:bg-[#232323] border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm mx-5 my-2">
                                <h4 className="text-lg font-semibold text-blue-700 dark:text-blue-400 px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                                    {t('categorization.title')}
                                </h4>
                                <div className="space-y-3 text-sm px-5 py-3">
                                    {Object.entries(grievance_bucket)
                                        // .filter(([k, v]) => k !== 'Description' && v)
                                        .filter(([k, v]) => {
                                            if (k === 'Description' || !v) return false;
                                            if (isNotSubcategory && k === 'SubCategory') return false;
                                            return true;
                                        })
                                        .map(([k, v], i) => (
                                            <div key={k} className="flex flex-col gap-1">
                                                <p className="font-medium text-gray-800 dark:text-gray-200">
                                                    {t(`categorization.${k}`) || k}
                                                </p>
                                                <p className="text-gray-700 dark:text-gray-300 bg-[#EBF2FF] dark:bg-blue-900/20 border border-[#6B71EA] px-3 py-2 rounded-md">
                                                    {String(v)}
                                                </p>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        {allFields.length > 0 && (
                            <div
                                ref={userInfoSectionRef}
                                tabIndex={-1}
                                className="relative bg-white dark:bg-[#232323] border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm mx-5 my-2 focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-hidden"
                            >
                                {/* Loading Overlay */}
                                {globalLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-black/60 z-20">
                                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex flex-col items-center justify-center text-center">
                                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mb-3"></div>
                                            <p className="text-gray-700 dark:text-gray-200 text-base font-medium">
                                                {t('loadingData') || 'Loading, please wait...'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* API Error Overlay */}
                                {apiError && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-black/60 z-20">
                                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex flex-col items-center justify-center text-center">
                                            <p className="text-red-600 text-base mb-3 font-medium">{apiError}</p>
                                            <button
                                                type="button"
                                                onClick={() => setApiError(null)}
                                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
                                            >
                                                {t('retry') || 'Retry'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Header */}
                                <div className="flex justify-between items-center px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                                    <h4 className="text-lg font-semibold text-[#1233A8] dark:text-green-400 flex items-center gap-2">
                                        {t('collectedUserInformation')}
                                    </h4>
                                    {/* <EditSaveButton
                                        isSaving={isSaving}
                                        isAutoSaving={isAutoSaving}
                                        hasUnsavedChanges={hasUnsavedChanges}
                                        isEditing={isEditing}
                                        onToggle={handleEditSaveToggle}
                                        t={t}
                                    /> */}

                                </div>

                                {/* Form Fields */}
                                <div className="space-y-4 text-sm px-5 py-4 relative z-10">
                                    {allFields.map((field) => (
                                        <div key={field.FieldName} className="flex flex-col gap-2">
                                            <label className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                {selectedLanguageData?.code === "en" ? field.DisplayLable : field.DisplayLable_regional}{" "}
                                                {field.IsMandatory && <span className="text-red-500">*</span>}
                                            </label>

                                            {renderField(field)}

                                            {errors[field.FieldName] && (
                                                <p className="text-sm text-red-600">{errors[field.FieldName]}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 mt-4 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
                        <button
                            onClick={closeModal}
                            className="px-4 py-2 text-white bg-red-500 hover:bg-red-600 rounded-md shadow transition-colors duration-200 font-medium"
                        >
                            {t('close')}
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting || isSaving || isGrievanceRegistering}
                            className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm font-medium px-6 py-2 rounded-md shadow transition-all duration-200 flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Submitting...
                                </>
                            ) : (
                                t('submit')
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

VerifyDataModal.propTypes = {
    handleSubmitGrievance: PropTypes.func,
    botResponse: PropTypes.object,
    isOpen: PropTypes.bool,
    closeModal: PropTypes.func,
    mandatory_data_present: PropTypes.any,
    updated_grievance: PropTypes.string,
    field_form: PropTypes.object,
    category_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    org_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    field_form_list: PropTypes.array,
    serverRemarks: PropTypes.string
};

export default VerifyDataModal;